import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, of } from 'rxjs';
import { catchError, concatMap, first, map, switchMap, tap } from 'rxjs/operators';

import { PurchaseConfirmedEvent } from '../../../models/events/purchase-confirmed.model';
import { ZenitEventName } from '../../../models/zenit-general.model';
import { ZenitEventApiService } from '../../../services/api/zenit-event-api.service';
import { ZenitEventParserService } from '../../../services/parser/zenit-event-parser.service';
import { ZenitEventError, ZenitEventSuccess } from '../../actions/zenit.actions';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { SetPage } from '@core/infrastructure/state/core-commands.actions';
import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { CoreStorageService } from '@core/services/storage/storage.service';
import { selectOrder } from '@features/order-confirmation/infrastructure/state/order-confirmation.selectors';
import { ZENIT_PURCHASE_EVENT_PENDING } from '@shared/analytics/store/checkout/analytics-checkout.effects';

@Injectable()
export class PurchaseConfirmedEventEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly zenitEventParserService: ZenitEventParserService,
        private readonly zenitEventApiService: ZenitEventApiService,
        private readonly store: Store,
        private readonly coreStorageService: CoreStorageService,

    ) { }

    public PurchaseConfirmedEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetPage),
            // eslint-disable-next-line no-secrets/no-secrets
            filter((action) => action.page === 'ItxAngularOrderConfirmationPage'),
            concatMap(() => this.store.select(getCmsConfigValue('enabled_zenit'))),
            filter(enabledZenit => enabledZenit),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap(() => this.store.select(selectOrder).pipe(
                first((value) => !!value)
            )),
            filter(({ id }) => this.isPurchaseEventPending(this.coreStorageService, id as number)),
            concatMap(() => this.zenitEventParserService.getEventObject<PurchaseConfirmedEvent>(
                ZenitEventName.PurchaseConfirmed,
            )),
            concatMap(purchaseConfirmed => this.zenitEventApiService.postEvent$(purchaseConfirmed, 'purchase-confirmed.input.v1')
                .pipe(
                    map(() => ZenitEventSuccess<PurchaseConfirmedEvent>()({ eventData: purchaseConfirmed })),
                    catchError((error) => of(ZenitEventError({ error }))),
                )),
        );
    });

    public ClearPurchaseEventPending$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ZenitEventSuccess<PurchaseConfirmedEvent>()),
            filter(({ eventData }) => eventData.eventName === ZenitEventName.PurchaseConfirmed),
            tap(() => { this.clearPurchaseEventPending(this.coreStorageService); }),
        );
    }, { dispatch: false });

    @BrowserOnly<PurchaseConfirmedEventEffects, undefined>(undefined)
    private isPurchaseEventPending(coreStorageService: CoreStorageService, orderId: number): boolean {
        return coreStorageService.getValue<number>(ZENIT_PURCHASE_EVENT_PENDING, 'SESSION') === orderId;
    }

    @BrowserOnly<PurchaseConfirmedEventEffects, undefined>(undefined)
    private clearPurchaseEventPending(coreStorageService: CoreStorageService): void {
        coreStorageService.removeValue(ZENIT_PURCHASE_EVENT_PENDING, 'SESSION');
    }
}
